import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createClient } from "@supabase/supabase-js";
import { useLocation, useNavigate } from "react-router-dom";
import { Link2, Settings2 } from "lucide-react";
import { useAuthSession } from "@/hooks/useAuthSession";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { acceptCommand, BRIDGE_URL, BRIDGE_KEY, commandRoute, recordTables, refreshTables, type NavCommand } from "@/lib/followChatGPT";

type Target = { command: NavCommand; record?: Record<string, unknown>; industry?: string };
type FollowContext = { follow: boolean; setFollow: (value: boolean) => void; configure: () => void; status: string; target: Target | null };
const Context = createContext<FollowContext | null>(null);
export const useFollowChatGPT = () => useContext(Context);
const read = (store: Storage, key: string) => { try { return store.getItem(key); } catch { return null; } };
const write = (store: Storage, key: string, value: string) => { try { store.setItem(key, value); } catch { /* Storage can be blocked. */ } };

export function FollowChatGPTProvider({ children }: { children: ReactNode }) {
  const { user, isReady } = useAuthSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [follow, setFollow] = useState(() => read(sessionStorage, "followChatGPT") !== "off");
  const [channel, setChannel] = useState(() => read(localStorage, "followChatGPTChannel") ?? "");
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("Not paired");
  const [error, setError] = useState("");
  const [target, setTarget] = useState<Target | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const seen = useRef(new Set<string>());
  const live = useRef({ follow, user, target, pathname: location.pathname });
  live.current = { follow, user, target, pathname: location.pathname };
  const configure = () => { setDraft(channel); setError(""); setOpen(true); };
  useEffect(() => { write(sessionStorage, "followChatGPT", follow ? "on" : "off"); }, [follow]);
  useEffect(() => {
    if (isReady && user && !channel && read(sessionStorage, "followChatGPTAsked") !== "yes") {
      write(sessionStorage, "followChatGPTAsked", "yes"); setOpen(true);
    }
    if (!user) { setTarget(null); setRecordOpen(false); setOpen(false); }
  }, [channel, isReady, user]);

  useEffect(() => {
    if (!channel || !user) return;
    let disposed = false;
    const bridge = createClient(BRIDGE_URL, BRIDGE_KEY, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: "salesby-bridge-listener" } });
    const ch = bridge.channel(channel);
    setStatus("Connecting");
    const stillAllowed = () => !disposed && live.current.follow && live.current.user?.id === user.id && document.visibilityState === "visible";
    ch.on("broadcast", { event: "navigate" }, async ({ payload }) => {
      const cmd = acceptCommand(payload, live.current.follow, document.visibilityState === "visible", seen.current);
      if (!cmd) return;
      let record: Record<string, unknown> | undefined;
      let industry: string | undefined;
      try {
        if (cmd.target in recordTables) {
          const table = recordTables[cmd.target as keyof typeof recordTables];
          const { data, error: queryError } = await supabase.from(table).select("*").eq("user_id", user.id).eq("id", cmd.id ?? "").maybeSingle();
          if (queryError || !data) { if (stillAllowed()) setError("That record is unavailable in your account."); return; }
          record = data as Record<string, unknown>;
        }
        if (cmd.industry) {
          const { data } = await supabase.from("industries").select("name").eq("user_id", user.id);
          industry = data?.find(row => row.name.trim().toLowerCase() === cmd.industry?.trim().toLowerCase())?.name;
          if (!industry && cmd.target === "industry") { if (stillAllowed()) setError("Industry not found in your account."); return; }
        }
        if (!stillAllowed() || Date.now() - Date.parse(cmd.issued_at) > 60_000) return;
        setError("");
        setTarget({ command: cmd, record, industry });
        setRecordOpen(!!record && !["client", "prospect", "trade_show"].includes(cmd.target));
        navigate(commandRoute(cmd), { state: { followChatGPT: true, commandId: cmd.command_id } });
        void ch.send({ type: "broadcast", event: "ack", payload: { command_id: cmd.command_id, at: new Date().toISOString() } });
      } catch { if (stillAllowed()) setError("Unable to open that record. Please try again."); }
    }).on("broadcast", { event: "refresh" }, async ({ payload }) => {
      if (disposed || !live.current.follow || !payload || typeof payload.table !== "string") return;
      const table = payload.table;
      if (!/^[a-z_]{1,80}$/.test(table)) return;
      window.dispatchEvent(new CustomEvent("salesby-refresh", { detail: { tables: refreshTables(table), id: payload.id } }));
      const current = live.current.target;
      if (!current?.record || !(current.command.target in recordTables)) return;
      const currentTable = recordTables[current.command.target as keyof typeof recordTables];
      if (currentTable !== table) return;
      const { data } = await supabase.from(currentTable).select("*").eq("user_id", user.id).eq("id", String(current.record.id)).maybeSingle();
      if (!disposed && live.current.follow && live.current.target?.command.command_id === current.command.command_id) {
        if (data) setTarget({ ...current, record: data as Record<string, unknown> });
        else setRecordOpen(false);
      }
    }).subscribe((state) => {
      if (disposed) return;
      setStatus(state === "SUBSCRIBED" ? "Connected" : state === "CHANNEL_ERROR" || state === "TIMED_OUT" ? "Connection unavailable" : "Connecting");
    });
    return () => { disposed = true; void bridge.removeChannel(ch); };
  }, [channel, user?.id, navigate]);

  return <Context.Provider value={{ follow, setFollow, configure, status, target }}>
    {children}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Follow ChatGPT</DialogTitle><DialogDescription>Salesby Bridge sync code</DialogDescription></DialogHeader>
        <Label htmlFor="bridge-sync-code">Sync code</Label>
        <Input id="bridge-sync-code" type="password" autoComplete="off" value={draft} onChange={e => setDraft(e.target.value)} />
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Later</Button>
          <Button onClick={() => {
            const value = draft.trim();
            if (!/^salesby-sync-[A-Za-z0-9_-]{16,256}$/.test(value)) { setError("Enter the full sync code from Salesby Bridge."); return; }
            write(localStorage, "followChatGPTChannel", value); setChannel(value); setOpen(false); setError("");
          }}><Link2 className="mr-2 h-4 w-4" />Connect</Button>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{target?.command.label || target?.command.target.replace(/_/g, " ") || "Record"}</DialogTitle><DialogDescription>Salesby record</DialogDescription></DialogHeader>
        <dl className="space-y-3">
          {Object.entries(target?.record ?? {}).filter(([key, value]) => key !== "user_id" && value !== null && value !== "").map(([key, value]) => <div key={key} className="border-b border-border pb-2">
            <dt className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, " ")}</dt>
            <dd className="text-sm whitespace-pre-wrap break-words">{typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}</dd>
          </div>)}
        </dl>
      </DialogContent>
    </Dialog>
  </Context.Provider>;
}

export function FollowChatGPTControls() {
  const ctx = useFollowChatGPT();
  if (!ctx) return null;
  return <div className="flex items-center gap-2 shrink-0">
    <Switch id="follow-chatgpt-toggle" checked={ctx.follow} onCheckedChange={ctx.setFollow} aria-label="Follow ChatGPT" />
    <Label htmlFor="follow-chatgpt-toggle" className="hidden lg:inline text-xs whitespace-nowrap">Follow ChatGPT</Label>
    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={ctx.configure} title={`Follow ChatGPT · ${ctx.status}`} aria-label={`Follow ChatGPT settings: ${ctx.status}`}><Settings2 className="h-4 w-4" /></Button>
  </div>;
}